import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FiFileText } from 'react-icons/fi';
import { Field, TextInput, SelectInput, Section } from './QuoteFields';
import { QuoteAddons } from './QuoteAddons';
import { AdminQuoteExtras } from './AdminQuoteExtras';
import { QuoteSummary } from './QuoteSummary';
import { quoteEndpoints, BACKEND, errMsg } from './quoteApi';

const VILLA_ORDER = ['3bhk', '4bhk', '5bhk', '7bhk', '8bhk', '9bhk', '12bhk'];
const EVENT_TYPES = ['Corporate Offsite / Team Retreat', 'Team Outing', 'Family Getaway', 'Birthday Celebration', 'Wedding / Pre-Wedding', 'Film / TV Shoot', 'Other'];
const localIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const today = () => localIso(new Date());
const addDays = (iso, n) => { const [y, m, dd] = iso.split('-').map(Number); return localIso(new Date(y, m - 1, dd + n)); };
const nightsOf = (a, b) => (a && b ? Math.round((new Date(b) - new Date(a)) / 86400000) : 0);

const initialForm = (fixedVillaId, initial) => ({
  client_name: '', client_phone: '', client_email: '', company: '', company_website: '',
  event_type: fixedVillaId ? EVENT_TYPES[0] : '', villa_id: fixedVillaId || '',
  check_in: '', check_out: '', guests: 10, check_in_time: '01:00 PM', check_out_time: '10:00 AM',
  addons: { cooking_days: 0, maid_days: 0, bbq_meals: 0, bbq_extra_labour: 0, bonfire_sessions: 0 },
  nightly_overrides: {}, special_discount: 0, special_discount_note: '', notes: '', ...initial,
});

const useQuoteMeta = () => {
  const [villas, setVillas] = useState([]);
  const [cookingInfo, setCookingInfo] = useState({ enabled: false, pricing: {} });
  useEffect(() => {
    axios.get(`${BACKEND}/api/villas`).then((r) => {
      const list = Array.isArray(r.data) ? r.data : r.data?.villas || [];
      setVillas([...list].sort((a, b) => VILLA_ORDER.indexOf(a.id.split('-')[0]) - VILLA_ORDER.indexOf(b.id.split('-')[0])));
    }).catch(() => {});
    axios.get(`${BACKEND}/api/features/cooking-amenity`).then((r) => setCookingInfo({ enabled: !!r.data?.enabled, pricing: r.data?.pricing || {} })).catch(() => {});
  }, []);
  return { villas, cookingInfo };
};

const useLiveCalc = (ep, f) => {
  const [calc, setCalc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const key = JSON.stringify([f.villa_id, f.check_in, f.check_out, f.guests, f.addons, f.nightly_overrides, f.special_discount, f.special_discount_note]);
  useEffect(() => {
    if (!f.villa_id || nightsOf(f.check_in, f.check_out) <= 0) { setCalc(null); setError(''); return undefined; }
    setLoading(true);
    const t = setTimeout(() => {
      ep.calc(f).then((r) => { setCalc(r.data); setError(''); })
        .catch((e) => { setCalc(null); setError(errMsg(e, 'Could not calculate')); })
        .finally(() => setLoading(false));
    }, 400);
    return () => clearTimeout(t);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return { calc, loading, error };
};

export const QuoteForm = ({ mode = 'guest', token, fixedVillaId, initial, onCreated }) => {
  const isAdmin = mode !== 'guest';
  const ep = useMemo(() => quoteEndpoints(mode, token), [mode, token]);
  const { villas, cookingInfo } = useQuoteMeta();
  const [f, setF] = useState(() => initialForm(fixedVillaId, initial));
  const set = (patch) => setF((p) => ({ ...p, ...patch }));
  const setAddon = (k, v) => setF((p) => ({ ...p, addons: { ...p.addons, [k]: v } }));
  const { calc, loading, error } = useLiveCalc(ep, f);
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState('');
  const villa = villas.find((v) => v.id === f.villa_id);
  const nights = nightsOf(f.check_in, f.check_out);

  const submit = async () => {
    setSubmitErr('');
    if (f.client_name.trim().length < 2) return setSubmitErr('Please enter the client / guest name');
    if (!isAdmin && f.client_phone.replace(/\D/g, '').length < 10) return setSubmitErr('Please enter a valid 10-digit mobile number');
    if (!calc) return setSubmitErr(error || 'Please select villa, dates and guests');
    setSubmitting(true);
    try {
      const r = await ep.create(f);
      onCreated(r.data);
    } catch (e) {
      setSubmitErr(errMsg(e, 'Could not generate quotation'));
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="quote-form" className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      <div className="lg:col-span-3 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Name *"><TextInput testid="quote-client-name" value={f.client_name} onChange={(e) => set({ client_name: e.target.value })} placeholder="Mr. Rahul Mehta" /></Field>
          <Field label={isAdmin ? 'Mobile' : 'Mobile *'}><TextInput testid="quote-client-phone" type="tel" value={f.client_phone} onChange={(e) => set({ client_phone: e.target.value })} placeholder="98XXXXXXXX" /></Field>
          <Field label="Email"><TextInput testid="quote-client-email" type="email" value={f.client_email} onChange={(e) => set({ client_email: e.target.value })} placeholder="you@company.com" /></Field>
          <Field label="Company"><TextInput testid="quote-company" value={f.company} onChange={(e) => set({ company: e.target.value })} placeholder="Company name (optional)" /></Field>
          <Field label="Company website"><TextInput testid="quote-company-website" value={f.company_website} onChange={(e) => set({ company_website: e.target.value })} placeholder="www.company.com" /></Field>
          <Field label="Event type">
            <SelectInput testid="quote-event-type" value={f.event_type} onChange={(e) => set({ event_type: e.target.value })}>
              <option value="">Select occasion</option>
              {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </SelectInput>
          </Field>
        </div>
        <Section title="Stay details">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label="Villa" className="col-span-2">
              {fixedVillaId ? (
                <div data-testid="quote-villa-fixed" className="bg-[#FFF7ED] border border-[#F57C20]/30 rounded-lg px-3 py-2.5 text-sm font-semibold text-[#1A1A2E]">
                  {villa ? `${villa.bedrooms}BHK ${villa.short_name}` : '12BHK Celebration Villa'}
                </div>
              ) : (
                <SelectInput testid="quote-villa-select" value={f.villa_id} onChange={(e) => set({ villa_id: e.target.value, nightly_overrides: {} })}>
                  <option value="">Select villa</option>
                  {villas.map((v) => <option key={v.id} value={v.id}>{`${v.bedrooms}BHK ${v.short_name} (up to ${v.capacity})`}</option>)}
                </SelectInput>
              )}
            </Field>
            <Field label="Check-in"><TextInput testid="quote-checkin" type="date" min={today()} value={f.check_in}
              onChange={(e) => { const v = e.target.value; set({ check_in: v, nightly_overrides: {}, ...(f.check_out <= v ? { check_out: addDays(v, 1) } : {}) }); }} /></Field>
            <Field label="Check-out"><TextInput testid="quote-checkout" type="date" min={f.check_in ? addDays(f.check_in, 1) : today()} value={f.check_out}
              onChange={(e) => set({ check_out: e.target.value, nightly_overrides: {} })} /></Field>
            <Field label={`Guests${villa ? ` (max ${villa.capacity})` : ''}`}><TextInput testid="quote-guests" type="number" min={1} max={villa?.capacity || 35} value={f.guests}
              onChange={(e) => set({ guests: parseInt(e.target.value, 10) || 1 })} /></Field>
          </div>
        </Section>
        <QuoteAddons addons={f.addons} setAddon={setAddon} nights={nights}
          cooking={{ enabled: cookingInfo.enabled, rate: cookingInfo.pricing[f.villa_id] || 0, villaPicked: !!f.villa_id }} />
        <AdminQuoteExtras f={f} set={set} calc={calc} isAdmin={isAdmin} />
      </div>
      <div className="lg:col-span-2">
        <div className="lg:sticky lg:top-0 bg-white border border-gray-200 rounded-xl p-4 space-y-4">
          <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-[#1A1A2E]">Live price breakdown</p>
          <QuoteSummary calc={calc} loading={loading} error={error} />
          {submitErr && <p data-testid="quote-submit-error" className="text-red-600 text-xs">{submitErr}</p>}
          <button data-testid="quote-generate-btn" onClick={submit} disabled={submitting}
            className="w-full bg-[#F57C20] hover:bg-[#E06A10] text-white px-6 py-3 rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
            <FiFileText className="w-4 h-4" /> {submitting ? 'Generating…' : 'Generate Quotation PDF'}
          </button>
          <p className="text-[11px] text-[#6B7280] text-center">Quote valid for 7 days · subject to availability</p>
        </div>
      </div>
    </div>
  );
};

export default QuoteForm;
